import React, { memo, useCallback } from 'react';

import { TeamNanoID } from '../../../../teams/teamsTypes';
import { ThreeDStockMaterialsListItemMaterial } from './ThreeDStockMaterialsListItem.types';
import {
  MaterialBrandID,
  MaterialNanoID,
  UpdateMaterialCacheAction
} from '../../../../materials/materialsTypes';
import { OnLightboxOpenAction } from '../../../../../helpers/LightboxWrapper';
import { ThreeDStockMaterialsListItemInfoMaterialsSet } from './components/ThreeDStockMaterialsListItemInfo/ThreeDStockMaterialsListItemInfo.types';
import {
  FetchMaterialsSetsCacheKeys,
  UpdateMaterialsSetCacheAction
} from '../../../../materialsSets/materialsSetsTypes';

import { ThreeDStockMaterialsListItemPreview } from './components/ThreeDStockMaterialsListItemPreview';
import { ThreeDStockMaterialsListItemInfo } from './components/ThreeDStockMaterialsListItemInfo';

import { MaterialsPermissions } from '../../../../materials/materialsConstants';
import { MaterialPath } from '../../../../materials/MaterialPath';
import { TeamPath } from '../../../../teams/TeamPath';

type ThreeDStockMaterialsListItemProps = {
  material: ThreeDStockMaterialsListItemMaterial;
  updateMaterialCache?: UpdateMaterialCacheAction<ThreeDStockMaterialsListItemMaterial>;
  onMaterialEditButtonMouseEnter?: (materialNanoId: MaterialNanoID) => void;
  onLightboxOpen: OnLightboxOpenAction;
  onMaterialBrandButtonClick: (brandId: MaterialBrandID) => void;
  materialsSet: ThreeDStockMaterialsListItemInfoMaterialsSet | null;
  materialsSetCacheKeys?: FetchMaterialsSetsCacheKeys;
  updateMaterialsSetCache: UpdateMaterialsSetCacheAction<ThreeDStockMaterialsListItemInfoMaterialsSet>;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
};

function ThreeDStockMaterialsListItem({
  material,
  updateMaterialCache,
  onMaterialEditButtonMouseEnter,
  onLightboxOpen,
  onMaterialBrandButtonClick,
  materialsSet,
  updateMaterialsSetCache,
  materialsSetCacheKeys,
  companyNanoId,
  isMyLibrary
}: ThreeDStockMaterialsListItemProps) {
  const handleOpenLightbox = useCallback(() => {
    if (material.image) {
      onLightboxOpen(material);
    }
  }, [material, onLightboxOpen]);

  const handleEditButtonMouseEnter = useCallback<() => void>(() => {
    onMaterialEditButtonMouseEnter(material.nanoId);
  }, [onMaterialEditButtonMouseEnter, material.nanoId]);

  let downloadButtonAction = MaterialsPermissions.READ_MATERIAL_DOWNLOAD_BUTTON;
  let downloadSeparatelyButtonAction =
    MaterialsPermissions.READ_MATERIAL_DOWNLOAD_SEPARATELY_BUTTON;
  let materialPagePath = MaterialPath.show(material.nanoId);
  let materialEditPagePath = MaterialPath.edit(material.nanoId);

  if (isMyLibrary) {
    materialPagePath = TeamPath.currentCompanyNewLibraryMaterial(
      material.nanoId
    );
    downloadButtonAction =
      MaterialsPermissions.READ_SELF_COMPANY_LIBRARY_MATERIAL_DOWNLOAD_BUTTON;
    downloadSeparatelyButtonAction =
      MaterialsPermissions.READ_SELF_COMPANY_LIBRARY_MATERIAL_DOWNLOAD_SEPARATELY_BUTTON;
  } else if (companyNanoId) {
    materialPagePath = TeamPath.companyNewLibraryMaterial(
      companyNanoId,
      material.nanoId
    );
    materialEditPagePath = TeamPath.editCompanyNewLibraryMaterial(
      companyNanoId,
      material.nanoId
    );
    downloadButtonAction =
      MaterialsPermissions.READ_OTHER_COMPANY_LIBRARY_MATERIAL_DOWNLOAD_BUTTON;
    downloadSeparatelyButtonAction =
      MaterialsPermissions.READ_OTHER_COMPANY_LIBRARY_MATERIAL_DOWNLOAD_SEPARATELY_BUTTON;
  }

  return (
    <div className="w-120 max-w-full flex flex-col relative rounded-md group">
      <div className="group rounded-xl bg-gray-100 dark:bg-gray-800 border dark:border-gray-700 has-[:checked]:ring-8 has-[:checked]:border-blue-500 dark:has-[:checked]:border-blue-500 has-[:checked]:bg-blue-500/20 dark:has-[:checked]:bg-blue-500/20 overflow-hidden relative flex flex-col">
        <ThreeDStockMaterialsListItemInfo
          material={material}
          onMaterialBrandButtonClick={onMaterialBrandButtonClick}
          materialsSet={materialsSet}
          updateMaterialsSetCache={updateMaterialsSetCache}
          materialsSetCacheKeys={materialsSetCacheKeys}
          materialPagePath={materialPagePath}
        />

        <ThreeDStockMaterialsListItemPreview
          material={material}
          onLightboxOpen={handleOpenLightbox}
          updateMaterialCache={updateMaterialCache}
          onEditButtonMouseEnter={handleEditButtonMouseEnter}
          downloadButtonAction={downloadButtonAction}
          downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
          materialEditPagePath={materialEditPagePath}
        />
      </div>
    </div>
  );
}

export default memo<ThreeDStockMaterialsListItemProps>(
  ThreeDStockMaterialsListItem
);
